/**
 * SINGLE SOURCE OF TRUTH for Better Auth Permissions
 *
 * This file contains all permission-related constants used across the platform.
 * NEVER hardcode permission counts or role lists - always import from here.
 */

/**
 * Complete list of all permissions in the system
 *
 * STRUCTURE:
 * - 1 Core permission (admin)
 * - 8 Valuations permissions (dashboard, reports, methods, valuations_sales, valuations_transfer, valuations_marketing, valuations_brand, approver)
 * - 5 Sponsorships permissions (sponsors, packages, inventory, proposals, analytics)
 * - 9 Platform permissions (users, organisation, app_settings, billing, plugins, api, sso, audit_log, documents)
 *
 * TOTAL: 23 permissions
 *
 * UPDATED: 2025-12-14 - Removed viewer permission (using && !isDemo pattern instead)
 */
export const ALL_PERMISSIONS = [
  // Core (1)
  'admin',

  // Valuations (8)
  'dashboard',
  'reports',
  'methods',
  'valuations_sales',
  'valuations_transfer',
  'valuations_marketing',
  'valuations_brand',
  'approver',

  // Sponsorships (5)
  'sponsors',
  'packages',
  'inventory',
  'proposals',
  'analytics',

  // Platform (9)
  'users',
  'organisation',
  'app_settings',
  'billing',
  'plugins',
  'api',
  'sso',
  'audit_log',
  'documents',
] as const;

/**
 * Maximum number of permissions a user can have
 * Automatically calculated from ALL_PERMISSIONS array
 */
export const MAX_PERMISSIONS = ALL_PERMISSIONS.length; // 23

/**
 * Permission type derived from the ALL_PERMISSIONS array
 */
export type Permission = (typeof ALL_PERMISSIONS)[number];

/**
 * Validate that a permission exists in the system
 */
export function isValidPermission(permission: string): permission is Permission {
  return ALL_PERMISSIONS.includes(permission as Permission);
}

/**
 * Get count of permissions by category
 */
export const PERMISSION_COUNTS = {
  core: 1, // admin only
  valuations: 8,
  sponsorships: 5,
  platform: 9,
  total: MAX_PERMISSIONS, // 23
} as const;
